import { HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigurationService } from '../../config/configuration.service';
import { AuthService } from '../../services/auth.service';
import { LoggerService } from '../../services/logger.service';
import { AuthRedirectDebugService } from '../auth-redirect-debug.service';
import { AuthRedirectReason } from '../auth-redirect-reason';
import { AuthEventType } from '../models/auth-events.model';
import { AuthSessionPolicy } from '../models/auth-session-policy.model';
import { isBiAlertRequest } from '../utils/auth-http.utils';
import { AuthLifecycleService } from './auth-lifecycle.service';
import { AuthSessionService } from './auth-session.service';
import { AuthTokenService } from './auth-token.service';
import { KeycloakClientService } from './keycloak-client.service';

@Injectable({
    providedIn: 'root',
})
export class AuthBootstrapService {
    constructor(
        private configurationService: ConfigurationService,
        private keycloakClient: KeycloakClientService,
        private authSession: AuthSessionService,
        private authToken: AuthTokenService,
        private authLifecycle: AuthLifecycleService,
        private authService: AuthService,
        private authRedirectDebug: AuthRedirectDebugService,
        private logger: LoggerService
    ) {}

    async initialize(): Promise<boolean> {
        this.registerBootstrapEventHandlers();

        const icoConfig = await this.configurationService.load();
        this.authRedirectDebug.configure(icoConfig.auth.redirectDebugEnabled);

        this.authSession.bindKeycloakEvents();

        const authenticated = await this.keycloakClient.init({
            config: icoConfig.auth.keycloak,
            initOptions: {
                onLoad: 'login-required',
                checkLoginIframe: icoConfig.auth.checkLoginIframe,
                pkceMethod: 'S256',
                redirectUri: window.location.origin + window.location.pathname,
            },
            updateMinValidity: icoConfig.auth.tokenMinValiditySeconds,
            shouldAddToken: (req: HttpRequest<unknown>): boolean => {
                const { method, url } = req;
                const isGetRequest = method.toUpperCase() === 'GET';
                const acceptablePaths = ['/assets', 'assets/', './assets', '/logout', '/forbidden'];
                const isAcceptablePathMatch = acceptablePaths.some(path => url.includes(path));

                return !(isGetRequest && isAcceptablePathMatch);
            },
            shouldUpdateToken: (req: HttpRequest<unknown>): boolean => {
                return !isBiAlertRequest(req.url, icoConfig.biAlertUrl);
            },
        });

        this.logger.debug('El usuario ya se encuentra autenticado? ' + authenticated);

        if (authenticated) {
            try {
                await this.authService.loadCurrentUser();
            } catch (error) {
                this.logger.error('Error al cargar el usuario', error);
            }

            this.authLifecycle.startIfAuthenticated();
        }

        return authenticated;
    }

    private registerBootstrapEventHandlers(): void {
        this.authSession.authEvents$.subscribe(event => {
            if (event.type === AuthEventType.OnTokenExpired) {
                this.logger.debug('El token ha expirado, intentando renovar');
                void this.handleTokenExpired();
            } else if (event.type === AuthEventType.OnAuthRefreshError) {
                this.logger.debug('No se pudo refrescar la sesion Keycloak, redirigiendo al logout');
                if (this.keycloakClient.isInitialized()) {
                    this.authSession.handleSessionEnded(
                        AuthRedirectReason.AuthRefreshError,
                        AuthSessionPolicy.FullLogout
                    );
                }
            } else if (event.type === AuthEventType.OnAuthLogout) {
                this.logger.debug('Sesion SSO finalizada en Keycloak');
                if (this.keycloakClient.isInitialized() && !window.location.pathname.includes('/logout')) {
                    this.authSession.handleSessionEnded(
                        AuthRedirectReason.SsoLogout,
                        AuthSessionPolicy.FullLogout
                    );
                }
            } else if (event.type === AuthEventType.OnReady) {
                this.logger.debug('KC esta Ready?', event.args);
            } else if (event.type === AuthEventType.OnAuthSuccess) {
                this.authLifecycle.startIfAuthenticated();
            }
        });
    }

    private async handleTokenExpired(): Promise<void> {
        if (!this.keycloakClient.isInitialized()) {
            return;
        }

        this.logger.debug(
            '[AuthToken] Bootstrap handling OnTokenExpired',
            JSON.stringify(this.keycloakClient.getRefreshDiagnostics())
        );

        try {
            await this.authToken.updateToken();
        } catch (error) {
            this.logger.warn(
                '[AuthToken] Bootstrap refresh after OnTokenExpired failed',
                error instanceof Error ? error.message : String(error)
            );
            // handleSessionEnded is invoked by AuthTokenService when refresh fails
        }
    }
}
